import { call, put, select, takeEvery } from 'redux-saga/effects';

import { Campaign, Ruleset } from 'entities/campaign';
import { Selectors as Router } from 'router';
import {
  Actions as CampaignActions,
  client as CampaignsClient,
  Selectors as Campaigns,
} from 'sections/campaigns/state/campaigns';
import {
  Actions as RulesetActions,
  client as RulesetsClient,
  Selectors as RulesetSelectors,
} from 'sections/campaigns/state/rulesets';
import { EntityWithId, IClient } from 'services/apiGatewayClient';
import i18next from 'i18next';

// ----------------------- Actions -----------------------------------------

enum Type {
  Clone = 'Campaigns:CloneCampaignRequest',
  Error = 'Campaigns:CloneCampaignError',
}

interface CloneCampaignRequestAction {
  type: Type.Clone;
  payload: {
    campaignId: string;
    name: string;
  };
}

interface CloneCampaignErrorAction {
  type: Type.Error;
  payload: {
    errors: string[];
  };
}

export type CloneCampaignAction = CloneCampaignRequestAction | CloneCampaignErrorAction;

export const cloneCampaign = (id: string, name: string): CloneCampaignAction => ({
  type: Type.Clone,
  payload: { campaignId: id, name },
});

const errorAction = (errors: string[]) => ({
  type: Type.Error,
  payload: { errors },
});

// ----------------------- Saga -------------------------------------------

function* clone(
  campaignsClient: IClient<Campaign>,
  rulesetsClient: IClient<Ruleset>,
  originalCampaignId: string,
  newCampaignName: string,
) {
  const siteId = yield select(Router.getSiteId);
  const originalCampaign = yield select(Campaigns.getEntity, originalCampaignId);

  let campaignId: string;
  let campaign: Campaign = {
    ...originalCampaign,
    name: newCampaignName,
    state: 'TESTING',
  };

  try {
    const response: EntityWithId<Campaign> = yield call(
      [campaignsClient, 'post'],
      `/mps/sites/${siteId}/campaigns`,
      campaign,
    );
    campaignId = response.id;
    campaign = response.entity;
  } catch (error) {
    yield put(
      CampaignActions.failCreate(
        undefined,
        i18next.t('common:errors.cantCreateCampaign', { error: (error as Error).message }),
      ),
    );
    return;
  }

  const rulesets = yield select(RulesetSelectors.getEntityMap);

  const idMap: { [oldId: string]: string } = {};
  const failed: string[] = [];

  for (const rulesetId of Object.keys(rulesets).filter(x => rulesets[x].campaignId === originalCampaignId)) {
    try {
      const response: EntityWithId<Ruleset> = yield call(
        [rulesetsClient, 'post'],
        `/mps/sites/${siteId}/campaigns/${campaignId}/message-rulesets`,
        rulesets[rulesetId],
      );
      idMap[rulesetId] = response.id;
      yield put(RulesetActions.completeCreate(undefined, response));
    } catch (err) {
      failed.push(rulesets[rulesetId].entity.name);
    }
  }

  campaign.rulesetPriorities = (originalCampaign.rulesetPriorities || [])
    .map((id: string) => idMap[id])
    .filter((id: string) => !!id);

  let updateFailed = false;
  try {
    const response: EntityWithId<Campaign> = yield call(
      [campaignsClient, 'put'],
      `/mps/sites/${siteId}/campaigns/${campaignId}`,
      campaign,
    );
    yield put(CampaignActions.completeCreate(undefined, response));
  } catch (error) {
    updateFailed = true;
  }

  const errors: string[] = failed.map(name => i18next.t('common:errors.failedCloneRuleset', { name }));
  if (!!updateFailed) {
    errors.push(i18next.t('common:errors.failedCloneCampaign'));
  }
  if (errors.length !== 0) {
    yield put(errorAction(errors));
  }
}

export function watchForClone(
  campaignsClient: IClient<Campaign> = CampaignsClient,
  rulesetsClient: IClient<Ruleset> = RulesetsClient,
) {
  return function*() {
    yield takeEvery(Type.Clone, (action: CloneCampaignRequestAction) =>
      clone(campaignsClient, rulesetsClient, action.payload.campaignId, action.payload.name),
    );
  };
}
